import { VideoEditorLogger } from '../../utility/VideoEditorLogger';
import { EditorChangeType } from '../types';
import { AbstractLayoutCommand } from './AbstractLayoutCommand';

import type { Element } from '../models/Element';
import type { VideoEditor } from '../VideoEditor';

export interface SplitLayoutElementParameters {
  splitTime: number;
}

export interface ResultElements {
  top: Element;
  bottom: Element;
  parent: Element;
}

export class SplitLayoutElementsCommand extends AbstractLayoutCommand {
  private params: SplitLayoutElementParameters;

  getName(): string {
    return 'SplitLayoutElementsCommand';
  }
  private _firstResultElements: ResultElements | null = null;
  private _secondResultElements: ResultElements | null = null;

  constructor(
    editor: VideoEditor,
    element: Element,
    params: SplitLayoutElementParameters,
  ) {
    super(editor, element);
    this.params = params;
  }

  execute(): boolean {
    if (!this.sourceElement) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'No source element provided',
      });
      return false;
    }

    super.execute(); // makes a snapshot

    const elements = this.pickAllLayoutElements(this.sourceElement);
    if (!elements) {
      VideoEditorLogger.logCommand(this, 'error', {
        error:
          'Failed to remove parent: no parent found or not a layout element',
      });
      return false;
    }
    const {
      parent: parentElement,
      children: childrenElements,
      data: layoutElementsData,
    } = elements;

    // Get top and bottom elements using the helper method
    const layoutElements = this.separateChildrenByPosition(childrenElements);
    if (!layoutElements) return false;
    const { top: topElement, bottom: bottomElement } = layoutElements;

    const trackManager = this.editor.getTrackManager();

    const parentSplitResult = trackManager.splitElement(
      parentElement,
      this.params.splitTime,
    );
    if (!parentSplitResult) return false;

    const topSplit = trackManager.splitElement(
      topElement,
      this.params.splitTime,
    );
    if (!topSplit) return false;

    const bottomSplit = trackManager.splitElement(
      bottomElement,
      this.params.splitTime,
    );
    if (!bottomSplit) return false;

    // Update extra element data with new relationships

    // Head section relationships
    this.extraElementData.setLayoutElements(
      parentSplitResult[0], // parent head
      topSplit[0], // top head
      bottomSplit[0], // bottom head
      layoutElementsData.layoutType ?? 'stacked-1-1',
    );
    // Tail section relationships
    this.extraElementData.setLayoutElements(
      parentSplitResult[1], // parent tail
      topSplit[1], // top tail
      bottomSplit[1], // bottom tail
      layoutElementsData.layoutType ?? 'stacked-1-1',
    );

    this._firstResultElements = {
      parent: parentSplitResult[0],
      top: topSplit[0],
      bottom: bottomSplit[0],
    };
    this._secondResultElements = {
      parent: parentSplitResult[1],
      top: topSplit[1],
      bottom: bottomSplit[1],
    };
    this.canUndo = true;

    // Recalculate timeline duration
    this.recalculateTimeline();

    // Notify observers about the split
    this.editor.notifyObservers(EditorChangeType.ELEMENT_SPLIT, {
      originalElement: this.sourceElement,
      firstElement: this._firstResultElements,
      secondElement: this._secondResultElements,
      splitTime: this.params.splitTime,
    });

    return true;
  }

  getDescription(): string {
    return `splitting layout element`;
  }

  getResultElements(): [ResultElements, ResultElements] | null {
    if (!this._firstResultElements || !this._secondResultElements) {
      return null;
    }
    return [this._firstResultElements, this._secondResultElements];
  }
}
